/**
 * Pure presentation model for an NPC's frozen **feature stat block**. At spawn, the non-weapon half
 * of an NPC class's `base_features` (its Traits / Systems / Reactions / Tech) freezes into
 * {@link CombatantBase.npcFeatures}; the unit sheet's loadout (`unitLoadout.ts`) and the player's
 * dossier ({@link PlayerNpcInspectorBody}) render them. This module owns the grouping — a pure value
 * transform, provable with no React/DOM (matching `attackIntent.ts` / `combatInspector.ts`). The
 * weapon half lives in `base.weapons`, so it never appears here.
 */
import type { Compendium } from "@lancer-vtt/compendium";
import type { FrozenNpcFeature } from "@lancer-vtt/domain";
import { isItemDestroyed } from "@lancer-vtt/domain";
import { NPC_FEATURE_GM_RULED, isGmRuledNpcFeature, type LoadedOverlay } from "@lancer-vtt/rules";

/** One section of the stat block — a feature `type` and the features that carry it, in spawn order. */
export type NpcFeatureGroup = {
  /** The feature category — `"Trait" | "System" | "Reaction" | "Tech" | …` (verbatim from the data). */
  type: string;
  /** The features of this `type`, in their frozen (input) order. */
  items: FrozenNpcFeature[];
};

/**
 * The canonical Lancer stat-block order. A `type` listed here sorts to its index; anything else
 * (an unexpected category from a third-party pack) sorts after, alphabetically — so the block is
 * always deterministic and a novel type still renders, just at the end.
 */
const TYPE_ORDER = ["Trait", "System", "Reaction", "Tech"] as const;

/**
 * Group a combatant's frozen NPC features into ordered stat-block sections. Preserves spawn order
 * within each `type`; orders the sections by {@link TYPE_ORDER} (canonical types first, novel types
 * alphabetically after). An absent/empty list yields `[]` (the inspector renders no block).
 */
export function groupNpcFeatures(features: readonly FrozenNpcFeature[] | undefined): NpcFeatureGroup[] {
  if (!features || features.length === 0) return [];
  const groups = new Map<string, FrozenNpcFeature[]>();
  for (const f of features) {
    const bucket = groups.get(f.type);
    if (bucket) bucket.push(f);
    else groups.set(f.type, [f]);
  }
  const rank = (type: string): number => {
    const i = TYPE_ORDER.indexOf(type as (typeof TYPE_ORDER)[number]);
    return i === -1 ? TYPE_ORDER.length : i;
  };
  return [...groups.entries()]
    .map(([type, items]) => ({ type, items }))
    .sort((a, b) => {
      const ra = rank(a.type);
      const rb = rank(b.type);
      return ra !== rb ? ra - rb : a.type.localeCompare(b.type);
    });
}

/**
 * The canonical Lancer feature kinds mapped to the short kind tag a unit sheet's loadout row prints
 * (the same tags as the mech rows' `WPN` / `SYS` / `TRT`). A kind not listed here (a novel
 * third-party category) falls back to its own uppercased `type`, so an unexpected kind still labels
 * honestly rather than going blank.
 */
const KIND_TAGS: Record<string, string> = {
  Trait: "TRT",
  System: "SYS",
  Reaction: "RCT",
  Tech: "TECH",
};

/** The short kind tag (`TRT | SYS | RCT | TECH`, else the uppercased type) a feature's loadout row prints. */
export function npcFeatureKindTag(type: string): string {
  return KIND_TAGS[type] ?? type.toUpperCase();
}

/**
 * The combatant's frozen NPC features as ONE flat list in canonical stat-block order — Trait, then
 * System, then Reaction, then Tech (novel types alphabetically after), spawn order preserved within
 * each type. Reuses {@link groupNpcFeatures} and flattens, for the unit sheet's loadout
 * (`unitLoadout.ts`), which prints one kind-tagged row per entry. An absent/empty list yields `[]`.
 */
export function orderedNpcFeatures(features: readonly FrozenNpcFeature[] | undefined): FrozenNpcFeature[] {
  return groupNpcFeatures(features).flatMap((g) => g.items);
}

/**
 * Is this frozen NPC feature's row DESTROYED? A `type === "System"` feature that froze into
 * {@link CombatantBase.equippedSystems} carries the twin key on `instanceId` (undefined for every
 * Trait/Reaction/Tech feature — those are never System-Trauma targets); `isItemDestroyed` reads
 * that key off `current.resources`, the SAME read every participation gate uses, so this can't
 * drift from the engine's own destroyed set. Pulled out as its own function (mirroring
 * {@link "./CombatWindows".classifySkirmishCard}) so the DESTROYED-tag decision is provable with
 * no React/DOM — the repo has no jsdom/headless renderer.
 */
export function isNpcFeatureDestroyed(
  feature: FrozenNpcFeature,
  resources: Record<string, number> | undefined,
): boolean {
  return feature.instanceId !== undefined && isItemDestroyed(resources, feature.instanceId);
}

/** The chip marker on a feature the engine does not carry out. Short; {@link NPC_FEATURE_GM_NOTE} explains it. */
export const NPC_FEATURE_GM_LABEL = "GM";

/** The explanation the marker's tooltip and the feature's tip body print. */
export const NPC_FEATURE_GM_NOTE = `${NPC_FEATURE_GM_RULED[0]!.toUpperCase()}${NPC_FEATURE_GM_RULED.slice(1)}. The app has this feature's rules text but does nothing with it.`;

/**
 * Is `feature` one the engine does not carry out, so the GM must rule it at the table (the
 * Sniper's Climber)? Data-driven, never a per-feature list: it is the mechanization
 * classifier's `unmechanized` verdict for the feature's frozen compendium id — rules text with
 * no authored overlay entry, no `ruledNarrative` marker, and no stat-bonus vector
 * (`packages/rules/src/encounter/effects/mechanization.ts`, the same derivation the pilot and
 * mech sheets' fiction-only chip reads). `false` while the overlay is still loading (a bare
 * `SEED_OVERLAY` would flag nearly everything) and for a feature frozen without an id.
 */
export type NpcFeatureGmRuledArgs = {
  feature: FrozenNpcFeature;
  compendium: Compendium;
  overlay: LoadedOverlay;
  loadingOverlay: boolean;
};

export function isNpcFeatureGmRuled(args: NpcFeatureGmRuledArgs): boolean {
  const { feature, compendium, overlay, loadingOverlay } = args;
  if (loadingOverlay || feature.id === undefined) return false;
  return isGmRuledNpcFeature({ featureId: feature.id, compendium, overlay });
}
